import { Button } from '@tunasong/ui-lib'
import { useState } from 'react'
import type { FC } from 'react'
import { useTheoryCopy } from '../theory-copy/use-theory-copy.js'
import type { SubMode } from './ear-training-params.js'
import styles from './ear-training-welcome.module.css'

export interface EarTrainingWelcomeProps {
  /** Pre-selected sub-mode from `?mode=` URL param, or `null` for no pre-selection. */
  preSelected: SubMode | null
  onStart: (subMode: SubMode) => void
}

/**
 * Ear Training Suite welcome screen — sub-mode picker (Interval ID vs
 * Chord-quality ID). Per Bowie §Q5, the session is locked to one sub-mode;
 * switching requires returning to welcome. Pre-selection comes from the
 * `?mode=` URL param but the user must still confirm with Start.
 *
 * Spec: docs/superpowers/specs/2026-05-20-ear-training-suite-design.md §4.2
 */
export const EarTrainingWelcome: FC<EarTrainingWelcomeProps> = props => {
  const { preSelected, onStart } = props
  const [selected, setSelected] = useState<SubMode | null>(preSelected)
  const intro = useTheoryCopy('ear-training-welcome-intro')
  return (
    <section className={styles.welcome} aria-label="Ear Training welcome">
      {intro?.body ? <p className={styles.intro}>{intro.body}</p> : null}
      <p className={styles.intro}>Pick a sub-mode below, then start a 10-question session.</p>
      <div className={styles.cardGrid}>
        <button
          type="button"
          className={styles.card}
          aria-pressed={selected === 'interval'}
          onClick={() => setSelected('interval')}
        >
          <h3 className={styles.cardTitle}>Interval ID</h3>
          <p className={styles.cardBody}>Hear two notes, identify the interval.</p>
          <p className={styles.cardMeta}>12 intervals · 10 questions</p>
        </button>
        <button
          type="button"
          className={styles.card}
          aria-pressed={selected === 'quality'}
          onClick={() => setSelected('quality')}
        >
          <h3 className={styles.cardTitle}>Chord-quality ID</h3>
          <p className={styles.cardBody}>Hear a chord, identify the quality.</p>
          <p className={styles.cardMeta}>7 qualities · 10 questions</p>
        </button>
      </div>
      <Button
        variant="contained"
        color="primary"
        size="large"
        disabled={selected === null}
        onClick={() => {
          if (selected !== null) {
            onStart(selected)
          }
        }}
      >
        Start session
      </Button>
    </section>
  )
}
